import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FaCamera, FaCheckCircle, FaPlus, FaTrash } from "react-icons/fa"; 
import Navbar from "./Navbar";
import DashboardHeader from "./DashboardHeader";
import api from "../api";
import Footer from "./Footer";
import { useTheme } from '../App';

/* ================= HELPERS ================= */
// REPLACE THIS with your actual backend URL where images are hosted
const backendUrl = "https://api.stdl.site"; 

const getInitialUnits = (userData) => {
  const str = userData?.unit || userData?.subDepartment || "";
  const arr = str.split(",").map(s => s.trim()).filter(Boolean);
  return arr.length > 0 ? arr : [""]; 
};

/* ================= LAYOUT ================= */
const AppContainer = styled.div`
  --sidebar-expanded: 260px;
  --sidebar-collapsed: 78px;
  
  /* 🔥 THEME VARIABLES 🔥 */
  --page-bg: ${({ $isDarkMode }) => $isDarkMode ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #edf4ff 100%)'};
  --card-bg: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : 'rgba(255, 255, 255, 0.96)'};
  --text-main: ${({ $isDarkMode }) => $isDarkMode ? '#f8fafc' : '#0f172a'};
  --text-soft: ${({ $isDarkMode }) => $isDarkMode ? '#94a3b8' : '#64748b'};
  --border: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(148, 163, 184, 0.18)'};
  --input-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#ffffff'};
  --input-border: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#e5e7eb'};
  --info-row-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#f8fafc'};
  --info-row-border: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#f1f5f9'};
  
  --shadow-lg: 0 22px 55px rgba(0, 0, 0, 0.15);
  --shadow-md: 0 12px 30px rgba(0, 0, 0, 0.1);

  min-height: 100vh;
  background: var(--page-bg);
  color: var(--text-main);
  display: flex;
  flex-direction: column;
  transition: background 0.3s ease, color 0.3s ease;
`;

const MainContent = styled.main`
  margin-left: ${(props) =>
    props.collapsed ? "var(--sidebar-collapsed)" : "var(--sidebar-expanded)"};
  padding: 92px 20px 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 86px 16px 20px;
  }

  @media (max-width: 576px) {
    padding: 80px 12px 20px;
  }
`;

const ProfileContainer = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  flex-grow: 1;
`;

/* ================= POPUP MODAL ================= */
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px; animation: fadeIn 0.25s ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const ModalContent = styled.div`
  background: var(--card-bg); width: min(400px, 100%); padding: 32px; border-radius: 24px;
  text-align: center; box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2); border: 1px solid var(--border);
  animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  @keyframes slideUp { from { transform: translateY(30px) scale(0.95); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }

  .icon { font-size: 50px; color: #10b981; margin-bottom: 16px; }
  h3 { margin: 0 0 10px; font-size: 1.4rem; font-weight: 800; color: var(--text-main); }
  p { margin: 0 0 24px; color: var(--text-soft); font-size: 0.95rem; line-height: 1.5; }
`;

const ModalButton = styled.button`
  width: 100%; padding: 14px; border-radius: 14px; border: none; background: #2563eb;
  color: white; font-size: 1rem; font-weight: 800; cursor: pointer; transition: all 0.2s ease;
  &:hover { background: #1d4ed8; transform: translateY(-2px); }
`;

/* ================= HERO ================= */
const Hero = styled.section` display: block; margin-bottom: 10px; `;

const HeroPanel = styled.div`
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%); color: white;
  padding: 32px 36px; border-radius: 20px; box-shadow: var(--shadow-lg); position: relative;
  overflow: hidden; display: flex; align-items: center; justify-content: space-between; gap: 0px;

  &::before {
    content: ""; position: absolute; right: -30px; bottom: -60px; width: 180px; height: 180px; 
    border-radius: 50%; background: rgba(255, 255, 255, 0.08);
  }

  @media (max-width: 900px) { flex-direction: column; text-align: center; padding: 32px 20px; }
`;

const HeroLeft = styled.div`
  display: flex; align-items: center; gap: 24px; z-index: 1;
  @media (max-width: 900px) { flex-direction: column; justify-content: center; }
`;

const HeroTitle = styled.h1` margin: 0; font-size: clamp(1.8rem, 1.5rem + 1.5vw, 2.8rem); line-height: 1.1; font-weight: 800; letter-spacing: -0.5px; `;
const HeroText = styled.p` margin: 6px 0 0; color: rgba(255, 255, 255, 0.85); font-size: 1.1rem; font-weight: 500; `;

const AvatarWrapper = styled.div` position: relative; width: 115px; height: 115px; @media (max-width: 768px) { width: 100px; height: 100px; } `;

const Avatar = styled.div`
  width: 100%; height: 100%; border-radius: 50%; background: #fff;
  border: 4px solid rgba(255, 255, 255, 0.95); overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const CameraIconWrapper = styled.label`
  position: absolute; bottom: 0; right: 0; background: #2563eb; color: white;
  width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  border: 3px solid white; cursor: pointer; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, background 0.2s ease;
  &:hover { transform: scale(1.1); background: #1d4ed8; }
  input { display: none; }
  @media (max-width: 768px) { width: 32px; height: 32px; bottom: -2px; right: -2px; }
`;

const AvatarFallback = styled.div`
  width: 100%; height: 100%; display: grid; place-items: center; background: rgba(255, 255, 255, 0.18);
  color: #1e40af; font-size: 2.5rem; font-weight: 900;
`;

const EditButton = styled.button`
  appearance: none; border: none; background: white; color: #1e40af; padding: 14px 28px;
  border-radius: 999px; font-weight: 800; font-size: 15px; cursor: pointer; transition: all 0.2s ease;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.12); z-index: 1;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 26px rgba(30, 64, 175, 0.22); }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
  @media (max-width: 900px) { width: 100%; max-width: 300px; margin-top: 10px; }
`;

/* ================= CONTENT GRID ================= */
const ContentGrid = styled.div` display: block;  `;

const Card = styled.section`
  background: var(--card-bg); border-radius: 24px; padding: 28px; box-shadow: var(--shadow-md); border: 1px solid var(--border);
  transition: background 0.3s ease, border 0.3s ease;
  @media (max-width: 576px) { padding: 20px; border-radius: 20px; }
`;

const CardTitle = styled.h3`
  font-size: 1.15rem; font-weight: 800; color: var(--text-main); margin: 0 0 24px; padding-bottom: 14px; border-bottom: 2px solid var(--border);
`;

/* ================= FORM / INFO ================= */
const FormGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px 16px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div` display: flex; flex-direction: column; gap: 8px; `;

const Label = styled.label` display: block; font-weight: 700; color: var(--text-soft); font-size: 14px; `;

const InputRow = styled.div` display: flex; align-items: center; gap: 10px; `;

const EditInput = styled.input`
  width: 100%; padding: 14px 16px; border: 2px solid var(--input-border); border-radius: 14px;
  font-size: 15px; transition: all 0.2s; background: var(--input-bg); color: var(--text-main);
  &:focus { background: var(--input-bg); border-color: #3b82f6; outline: none; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15); }
`;

const RemoveButton = styled.button`
  appearance: none; background: rgba(239, 68, 68, 0.1); color: #ef4444; border: none; border-radius: 12px;
  width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
  &:hover { background: #fca5a5; color: #b91c1c; }
`;

const AddButton = styled.button`
  appearance: none; background: transparent; color: #2563eb; border: 2px dashed #bfdbfe; padding: 12px; border-radius: 14px;
  font-weight: 700; font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: all 0.2s; margin-top: 4px;
  &:hover { background: rgba(37, 99, 235, 0.1); border-color: #3b82f6; }
`;

const WarningText = styled.div` color: #ef4444; font-size: 13px; font-weight: 600; margin-top: 4px; `;

const InfoGrid = styled.div` display: grid; gap: 12px; `;

const InfoRow = styled.div`
  display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: 16px; align-items: center; padding: 14px 0; border-bottom: 1px solid var(--info-row-border);
  &:last-child { border-bottom: none; }
  @media (max-width: 700px) { grid-template-columns: 1fr; gap: 6px; background: var(--info-row-bg); padding: 12px 16px; border-radius: 12px; border-bottom: none; }
`;

const InfoLabel = styled.div` font-weight: 700; color: var(--text-soft); font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; `;

const Value = styled.div` font-weight: 800; color: var(--text-main); font-size: 15px; line-height: 1.5; word-break: break-word; `;

const UnitPillList = styled.div` display: flex; flex-wrap: wrap; gap: 8px; `;
const UnitPill = styled.span` background: rgba(37, 99, 235, 0.1); color: #3b82f6; border: 1px solid rgba(59, 130, 246, 0.3); padding: 6px 12px; border-radius: 8px; font-size: 14px; font-weight: 700; `;

const ActionRow = styled.div`
  display: flex; justify-content: flex-end; gap: 12px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--border);
  @media (max-width: 576px) { flex-direction: column-reverse;  width: 100%; button { width: 100%; padding: 16px;  font-size: 1.05rem; } }
`;

const SecondaryButton = styled.button`
  appearance: none; border: 2px solid var(--input-border); background: transparent; color: var(--text-soft);
  padding: 12px 24px; border-radius: 14px; font-weight: 800; font-size: 14px; cursor: pointer; transition: all 0.2s ease;
  &:hover:not(:disabled) { background: rgba(148, 163, 184, 0.1); color: var(--text-main); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  appearance: none; border: none; background: linear-gradient(135deg, #1d4ed8, #3b82f6); color: white;
  padding: 12px 32px; border-radius: 14px; font-weight: 800; font-size: 14px; cursor: pointer; box-shadow: 0 10px 20px rgba(37, 99, 235, 0.18); transition: all 0.2s ease;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(37, 99, 235, 0.24); }
  &:disabled { opacity: 0.7; cursor: not-allowed; box-shadow: none; transform: none; }
`;

/* ================= COMPONENT ================= */
const Profile = ({ user, setUser, collapsed, setCollapsed }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [unitWarning, setUnitWarning] = useState("");
  
  // 🔥 FETCH GLOBAL THEME STATE 🔥
  const { isDarkMode } = useTheme();
  
  const [selectedFile, setSelectedFile] = useState(null);

  const [profileData, setProfileData] = useState({
    title: "", firstName: "", surname: "", gender: "", kingschatUsername: "", rank: "", department: "", supervisor: "", picture: "", subDepartmentUnits: [""], 
  });

  useEffect(() => {
    setProfileData({
      title: user?.title || "", firstName: user?.firstName || "", surname: user?.surname || "", gender: user?.gender || "",
      kingschatUsername: user?.kingschatUsername || "", rank: user?.rank || "", department: user?.department || "",
      supervisor: user?.supervisor || "", picture: user?.profilePicture || "", subDepartmentUnits: getInitialUnits(user),
    });
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubDepChange = (index, value) => {
    const newArr = [...profileData.subDepartmentUnits];
    newArr[index] = value;
    setProfileData((prev) => ({ ...prev, subDepartmentUnits: newArr }));
    if (unitWarning) setUnitWarning(""); 
  };

  const addSubDep = () => {
    const units = profileData.subDepartmentUnits;
    if (units.length > 0 && units[units.length - 1].trim() === "") {
      setUnitWarning("Please fill in the current sub-department / unit before adding a new one.");
      return;
    }
    setProfileData((prev) => ({ ...prev, subDepartmentUnits: [...prev.subDepartmentUnits, ""] }));
    setUnitWarning("");
  };

  const removeSubDep = (index) => {
    const newArr = [...profileData.subDepartmentUnits];
    newArr.splice(index, 1);
    setProfileData((prev) => ({ ...prev, subDepartmentUnits: newArr }));
    if (unitWarning) setUnitWarning(""); 
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfileData((prev) => ({ ...prev, picture: imageUrl }));
      setSelectedFile(file); 
    }
  };

  const handleCancel = () => {
    setProfileData({
      title: user?.title || "", firstName: user?.firstName || "", surname: user?.surname || "", gender: user?.gender || "",
      kingschatUsername: user?.kingschatUsername || "", rank: user?.rank || "", department: user?.department || "",
      supervisor: user?.supervisor || "", picture: user?.profilePicture || "", subDepartmentUnits: getInitialUnits(user),
    });
    setUnitWarning("");
    setSelectedFile(null); 
    setIsEditing(false);
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const validUnits = profileData.subDepartmentUnits.filter((u) => u.trim() !== "");
      const joinedUnits = validUnits.join(", ");
      const formData = new FormData();

      formData.append("title", profileData.title || ""); formData.append("firstName", profileData.firstName || "");
      formData.append("surname", profileData.surname || ""); formData.append("gender", profileData.gender || "");
      formData.append("kingschatUsername", profileData.kingschatUsername || ""); formData.append("rank", profileData.rank || "");
      formData.append("department", profileData.department || ""); formData.append("supervisor", profileData.supervisor || "");
      formData.append("unit", joinedUnits); formData.append("subDepartment", joinedUnits);

      if (selectedFile) formData.append("profilePicture", selectedFile);

      const res = await api.put("/auth/profile", formData);

      if (res.data.success) {
        try {
          const fetchRes = await api.get("/auth/profile");
          if (fetchRes.data.success && fetchRes.data.user) {
            const latestUser = fetchRes.data.user;
            localStorage.setItem("user", JSON.stringify(latestUser));
            if (setUser) setUser(latestUser);
            setProfileData({
              title: latestUser.title || "", firstName: latestUser.firstName || "", surname: latestUser.surname || "",
              gender: latestUser.gender || "", kingschatUsername: latestUser.kingschatUsername || "", rank: latestUser.rank || "",
              department: latestUser.department || "", supervisor: latestUser.supervisor || "", picture: latestUser.profilePicture || "",
              subDepartmentUnits: latestUser.unit && latestUser.unit.trim() !== "" ? latestUser.unit.split(",").map((u) => u.trim()).filter(Boolean) : [""],
            });
          }
        } catch (fetchErr) {
          console.error("Updated successfully, but failed to fetch fresh data", fetchErr);
        }

        setSelectedFile(null);
        setPopupMessage("Your profile information has been successfully updated.");
        setShowPopup(true);
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Profile update error:", err);
      alert(err.response?.data?.message || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setIsEditing(false);
  };

  const initials = `${profileData.firstName?.[0] || ""}${profileData.surname?.[0] || ""}`.toUpperCase();

  return (
    // 🔥 PASS THE THEME STATE TO THE CONTAINER 🔥
    <AppContainer $isDarkMode={isDarkMode}>
      <Navbar user={user} collapsed={collapsed} setCollapsed={setCollapsed} />
      <DashboardHeader user={user} collapsed={collapsed} title="My Profile" />

      {/* SUCCESS POPUP MODAL */}
      {showPopup && (
        <ModalOverlay>
          <ModalContent>
            <FaCheckCircle className="icon" />
            <h3>Profile Updated!</h3>
            <p>{popupMessage}</p>
            <ModalButton onClick={closePopup}>Awesome, thanks!</ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}

      <MainContent collapsed={collapsed}>
        <ProfileContainer>
          
        <Hero>
          <HeroPanel>
            <HeroLeft>
              <AvatarWrapper>
                <Avatar>
                  {profileData.picture ? (
                    <img 
                      src={
                        profileData.picture.startsWith("blob:") 
                          ? profileData.picture 
                          : profileData.picture.startsWith("http") 
                            ? profileData.picture 
                            : `${backendUrl}${profileData.picture.startsWith('/') ? '' : '/'}${profileData.picture}`
                      } 
                      alt="Profile"
                      onError={(e) => {
                        console.error("Failed to load image:", e.target.src);
                        e.target.src = ""; 
                      }}
                    />
                  ) : (
                    <AvatarFallback>{initials || "U"}</AvatarFallback>
                  )}
                </Avatar>
                
                {isEditing && (
                  <CameraIconWrapper>
                    <FaCamera size={14} />
                    <input type="file" accept="image/*" onChange={handleImageChange} />
                  </CameraIconWrapper>
                )}
              </AvatarWrapper>

              <div>
                <HeroTitle>Profile</HeroTitle>
                <HeroText>
                  {[profileData.title, profileData.firstName, profileData.surname]
                    .filter(Boolean)
                    .join(" ") || "Your Name"}
                </HeroText>
              </div>
            </HeroLeft>

            {!isEditing && (
              <div style={{ position: "relative", zIndex: 1 }}>
                <EditButton onClick={() => setIsEditing(true)} disabled={loading}>
                  Edit Profile
                </EditButton>
              </div>
            )}
          </HeroPanel>
        </Hero>

          <ContentGrid>
            <Card>
              <CardTitle>Personal Information</CardTitle>

              {isEditing ? (
                <>
                  <FormGrid>
                    {/* ROW 1 */}
                    <FormGroup>
                      <Label>Title</Label>
                      <EditInput name="title" value={profileData.title} onChange={handleInputChange} placeholder="e.g. Mr, Mrs, Dr" />
                    </FormGroup>

                    <FormGroup>
                      <Label>First Name</Label>
                      <EditInput name="firstName" value={profileData.firstName} onChange={handleInputChange} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Surname</Label>
                      <EditInput name="surname" value={profileData.surname} onChange={handleInputChange} />
                    </FormGroup>

                    {/* ROW 2 */}
                    <FormGroup>
                      <Label>Gender</Label>
                      <EditInput name="gender" value={profileData.gender} onChange={handleInputChange} placeholder="e.g. Male, Female" />
                    </FormGroup>

                    <FormGroup>
                      <Label>KingsChat Username</Label>
                      <EditInput name="kingschatUsername" value={profileData.kingschatUsername} onChange={handleInputChange} placeholder="@username" />
                    </FormGroup>

                    <FormGroup>
                      <Label>Rank</Label>
                      <EditInput name="rank" value={profileData.rank} onChange={handleInputChange} />
                    </FormGroup>

                    {/* ROW 3 */}
                    <FormGroup>
                      <Label>Department</Label>
                      <EditInput name="department" value={profileData.department} onChange={handleInputChange} />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>Name of Supervisor</Label>
                      <EditInput name="supervisor" value={profileData.supervisor} onChange={handleInputChange} />
                    </FormGroup>

                    {/* DYNAMIC MULTI-INPUT FIELD */}
                    <FormGroup style={{ gridColumn: "1 / -1" }}>
                      <Label>Sub-department, Unit</Label>
                      
                      {profileData.subDepartmentUnits.map((item, index) => (
                        <InputRow key={index}>
                          <EditInput value={item} onChange={(e) => handleSubDepChange(index, e.target.value)} placeholder="e.g. Frontend Development" />
                          {profileData.subDepartmentUnits.length > 1 && (
                            <RemoveButton type="button" onClick={() => removeSubDep(index)}>
                              <FaTrash />
                            </RemoveButton>
                          )}
                        </InputRow>
                      ))}

                      {unitWarning && <WarningText>{unitWarning}</WarningText>}

                      <AddButton type="button" onClick={addSubDep}>
                        <FaPlus size={12} /> Add another Sub-department, Unit 
                      </AddButton>
                    </FormGroup>
                  </FormGrid>

                  <ActionRow>
                    <SecondaryButton onClick={handleCancel} disabled={loading} type="button">Cancel</SecondaryButton>
                    <PrimaryButton onClick={handleSave} disabled={loading} type="button">
                      {loading ? "Saving..." : "Save Changes"}
                    </PrimaryButton>
                  </ActionRow>
                </>
              ) : (
                <InfoGrid>
                  <InfoRow>
                    <InfoLabel>Title</InfoLabel>
                    <Value>{profileData.title || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>First Name</InfoLabel>
                    <Value>{profileData.firstName || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Surname</InfoLabel>
                    <Value>{profileData.surname || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Gender</InfoLabel>
                    <Value>{profileData.gender || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>KingsChat Username</InfoLabel>
                    <Value>{profileData.kingschatUsername || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Rank</InfoLabel>
                    <Value>{profileData.rank || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Department</InfoLabel>
                    <Value>{profileData.department || "—"}</Value>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Name of Supervisor</InfoLabel>
                    <Value>{profileData.supervisor || "—"}</Value>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>Sub-department, Unit </InfoLabel>
                    <Value>
                      {profileData.subDepartmentUnits.length > 0 && profileData.subDepartmentUnits[0] !== "" ? (
                        <UnitPillList>
                          {profileData.subDepartmentUnits.filter(u => u.trim() !== "").map((unit, idx) => (
                            <UnitPill key={idx}>{unit}</UnitPill>
                          ))}
                        </UnitPillList>
                      ) : (
                        "—"
                      )}
                    </Value>
                  </InfoRow>
                </InfoGrid>
              )}
            </Card>
          </ContentGrid>
        </ProfileContainer>
         <Footer />
      </MainContent>
    </AppContainer>
  );
};

export default Profile;